<script lang="ts">
  import type { Snippet } from 'svelte';
  import { showGlobalError } from '../../errorBanner.svelte';

  let {
    onFiles,                  // direct File objects; upload happens outside WebSocket
    palette,                  // theme palette
    disabled = false,         // deshabilitado cuando el chat está busy
    overlayTitle = 'Suelta tus archivos aquí',
    overlayHint = 'Hasta 3 imágenes o PDF',
    children,                 // Svelte 5 snippet: contenido envuelto
  } = $props<{
    onFiles: (files: File[]) => void;
    palette: Record<string, string>;
    disabled?: boolean;
    overlayTitle?: string;
    overlayHint?: string;
    children: Snippet;
  }>();

  let isDragOver = $state(false);

  const ACCEPTED_TYPES = [
    'image/jpeg', 'image/png', 'image/webp', 'image/tiff',
    'image/bmp', 'image/gif', 'application/pdf',
  ];

  const MAX_FILE_SIZE = 49 * 1024 * 1024; // 49 MB

  function handleDragOver(e: DragEvent) {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = disabled ? 'none' : 'copy';
    if (disabled) return;
    isDragOver = true;
  }

  function handleDragLeave(e: DragEvent) {
    e.preventDefault();
    const el = e.currentTarget as HTMLElement;
    const related = e.relatedTarget as HTMLElement | null;
    if (!related || !el.contains(related)) {
      isDragOver = false;
    }
  }

  function handleDrop(e: DragEvent) {
    e.preventDefault();
    isDragOver = false;
    if (disabled) return;

    const files = Array.from(e.dataTransfer?.files ?? []);
    if (files.length === 0) return;
    // El límite de cantidad se valida en cada consumidor: aquí ≤3 se adjuntan
    // al mensaje y >3 se enrutan a la base documental (ver ChatView).
    for (const file of files) {
      if (file.type && !ACCEPTED_TYPES.includes(file.type)) {
        showGlobalError(`Formato de ${file.name} no soportado.`);
        return;
      }
      if (file.size <= 0 || file.size > MAX_FILE_SIZE) {
        showGlobalError(`${file.name} excede el límite de 49 MB.`);
        return;
      }
    }
    onFiles(files);
  }
</script>

<div
  class="drop-zone-wrapper"
  role="region"
  aria-label="Zona para adjuntar documentos"
  ondragover={handleDragOver}
  ondragleave={handleDragLeave}
  ondrop={handleDrop}
>
  {@render children()}

  {#if isDragOver && !disabled}
    <div class="drop-overlay">
      <div class="drop-box" style={`border-color: ${palette.accent}; color: ${palette.accent};`}>
        <i class="bi bi-cloud-upload" style="font-size: 3rem;"></i>
        <p>{overlayTitle}</p>
        <p class="drop-hint">{overlayHint}</p>
      </div>
    </div>
  {/if}
</div>

<style>
  .drop-zone-wrapper {
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
  }

  .drop-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: fade-in 0.15s ease;
    /* Puramente visual: no debe interceptar el puntero. El drop sigue
       disparándose sobre el contenido subyacente y burbujea al wrapper,
       lo que permite que zonas anidadas (p. ej. la Base documental)
       reciban su propio drop por encima de este overlay. */
    pointer-events: none;
  }

  .drop-box {
    border: 3px dashed;
    border-radius: 20px;
    padding: 48px 64px;
    text-align: center;
    background: rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(4px);
    pointer-events: none;
  }

  .drop-box p {
    margin: 8px 0 0 0;
    font-size: 1.1rem;
    font-weight: 600;
  }

  .drop-hint {
    font-size: 0.85rem !important;
    font-weight: 400 !important;
    opacity: 0.7;
  }

  @keyframes fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
</style>
